/**
 * Tracking Analytics Service
 *
 * Orchestrates GA4 + PostHog analytics providers.
 * Port of useAnalytics composable from Nuxt 3 → class-based for Nuxt 2.
 */

import type { AnalyticsProvider, ProviderFilter } from '~/types/tracking'

const isDev = process.env.NODE_ENV === 'development'

export class TrackingAnalyticsService {
  private providers: AnalyticsProvider[] = []

  registerProvider(provider: AnalyticsProvider): void {
    if (this.providers.some((p) => p.name === provider.name)) {
      if (isDev) {
        console.warn(`Analytics provider "${provider.name}" already registered`)
      }
      return
    }

    this.providers.push(provider)

    if (provider.init) {
      try {
        provider.init()
      } catch (error) {
        if (isDev) {
          console.warn(`Failed to initialize ${provider.name} provider:`, error)
        }
      }
    }

    if (isDev) {
      console.log(`Analytics provider registered: ${provider.name}`)
    }
  }

  identify(userId: string, properties?: Record<string, unknown>, filter?: ProviderFilter): void {
    const targets = this.getProviders(filter)
    for (const provider of targets) {
      try {
        provider.identify(userId, properties)
      } catch (error) {
        if (isDev) {
          console.warn(`Failed to identify user in ${provider.name}:`, error)
        }
      }
    }
  }

  reset(filter?: ProviderFilter): void {
    const targets = this.getProviders(filter)
    for (const provider of targets) {
      try {
        provider.reset()
      } catch (error) {
        if (isDev) {
          console.warn(`Failed to reset ${provider.name}:`, error)
        }
      }
    }
  }

  track(event: string, properties?: Record<string, unknown>, filter?: ProviderFilter): void {
    const targets = this.getProviders(filter)
    for (const provider of targets) {
      try {
        provider.track?.(event, properties)
      } catch (error) {
        if (isDev) {
          console.warn(`Failed to track event in ${provider.name}:`, error)
        }
      }
    }
  }

  setUserProperties(properties: Record<string, unknown>, filter?: ProviderFilter): void {
    const targets = this.getProviders(filter)
    for (const provider of targets) {
      try {
        provider.setUserProperties?.(properties)
      } catch (error) {
        if (isDev) {
          console.warn(`Failed to set user properties in ${provider.name}:`, error)
        }
      }
    }
  }

  group(
    groupType: string,
    groupKey: string,
    properties?: Record<string, unknown>,
    filter?: ProviderFilter
  ): void {
    const targets = this.getProviders(filter)
    for (const provider of targets) {
      try {
        provider.group?.(groupType, groupKey, properties)
      } catch (error) {
        if (isDev) {
          console.warn(`Failed to set group in ${provider.name}:`, error)
        }
      }
    }
  }

  private getProviders(filter?: ProviderFilter): AnalyticsProvider[] {
    if (!filter) return this.providers
    return this.providers.filter(filter)
  }
}
